/* frontend/src/pages/VehicleList.js */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Eye, Upload, Search, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../api';
import { PageHeader } from '../components/common';
import { ShadcnDataTable } from '../components/ShadcnDataTable';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { useToast } from "../lib/use-toast.js";
import ImportExcelModal from '../components/ImportExcelModal';
import { VEHICLE_STATUS_LABELS, VEHICLE_STATUS_CLASSES, VEHICLE_STATUS_OPTIONS } from '../utils/vehicleStatuses';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Pagination } from '../components/Pagination';

export default function VehicleList() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [placaFilter, setPlacaFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    loadVehicles();
  }, [currentPage]);

  // Busca com filtros (placa e status) com debounce
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      // Resetar para a primeira página ao mudar filtros
      setCurrentPage(1);
      searchWithFilters(1);
    }, 500); // 500ms de delay

    return () => clearTimeout(delayedSearch);
  }, [placaFilter, statusFilter]);

  const searchWithFilters = async (pageToLoad = currentPage) => {
    try {
      setLoading(true);
      
      // Monta os parâmetros da query
      const params = new URLSearchParams();
      if (placaFilter.trim()) {
        params.append('placa', placaFilter.trim());
      }
      if (statusFilter) {
        params.append('status', statusFilter);
      }
      params.append('page', pageToLoad);
      params.append('limit', 20);
      
      const queryString = params.toString();
      const url = `/veiculos?${queryString}`;
      
      const response = await api.get(url);
      const { vehicles, pagination: paginationData } = response.data;
      setFilteredData(vehicles);
      setPagination(paginationData);
    } catch (error) {
      console.error('Error searching vehicles:', error);
      toast({
        variant: "destructive",
        title: t('vehicles.searchError'),
        description: t('vehicles.searchErrorDescription')
      });
    } finally {
      setLoading(false);
    }
  };

  const loadVehicles = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/veiculos?page=${currentPage}&limit=20`);
      const { vehicles, pagination: paginationData } = response.data;
      setData(vehicles);
      setFilteredData(vehicles);
      setPagination(paginationData);
    } catch (error) {
      console.error('Error loading vehicles:', error);
      toast({
        variant: "destructive",
        title: t('vehicles.loadError'),
        description: t('vehicles.loadErrorDescription')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (vehicle) => {
    setVehicleToDelete(vehicle);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!vehicleToDelete) return;
    
    try {
      setDeleting(true);
      await api.delete(`/veiculos/${vehicleToDelete._id}`);
      
      toast({
        title: t('vehicles.vehicleDeleted'),
        description: t('vehicles.vehicleDeletedDesc', { placa: vehicleToDelete.placa })
      });
      
      // Recarregar a lista
      loadVehicles();
      
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toast({
        variant: "destructive",
        title: t('vehicles.deleteError'),
        description: t('vehicles.deleteErrorDescription')
      });
    } finally {
      setDeleting(false);
      setDeleteModalOpen(false);
      setVehicleToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setVehicleToDelete(null);
  };

  const columns = [
    { 
      field: 'modelo', 
      headerName: t('vehicleForm.fields.model'), 
      flex: 1,
      renderCell: (row) => row?.infoTecnica?.modelo || '-'
    },
    { 
      field: 'placa', 
      headerName: t('vehicleForm.fields.plate'), 
      width: 120,
      renderCell: (row) => row?.placa || '-'
    },
    { 
      field: 'marca', 
      headerName: t('vehicleForm.fields.brand'), 
      flex: 1,
      renderCell: (row) => row?.infoTecnica?.marca || '-'
    },
    { 
      field: 'ano', 
      headerName: t('vehicleForm.fields.year'), 
      width: 100,
      renderCell: (row) => row?.infoTecnica?.anoFabricacao || '-'
    },
    { 
      field: 'locadora', 
      headerName: 'Locadora', 
      flex: 1,
      renderCell: (row) => row?.historico?.locadoraNome || '-'
    },
    { 
      field: 'patio', 
      headerName: 'Pátio', 
      flex: 1.5,
      renderCell: (row) => {
        const patioNome = row?.historico?.patioNome;
        if (!patioNome || patioNome === '-') return '-';
        
        // Truncar endereço longo para exibição na tabela
        return (
          <span title={patioNome} className="truncate block">
            {row?.historico?.patioCidade || patioNome}
          </span>
        );
      }
    },
    { 
      field: 'preco', 
      headerName: t('vehicleForm.fields.price'), 
      flex: 1,
      renderCell: (row) => {
        const valor = row?.comercial?.valorVenda || 0;
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(valor);
      }
    },
    { 
      field: 'quilometragem', 
      headerName: t('vehicleForm.fields.mileage'), 
      flex: 1,
      renderCell: (row) => {
        const km = row?.infoTecnica?.km || 0;
        return `${km.toLocaleString('pt-BR')} km`;
      }
    },
    { 
      field: 'status', 
      headerName: t('vehicleForm.fields.status'), 
      flex: 1,
      renderCell: (row) => {
        const status = row?.historico?.status;
        
        return (
          <span className={`px-2 py-1 text-xs rounded-[4px] ${VEHICLE_STATUS_CLASSES[status] || 'bg-gray-100 text-gray-800'}`}>
            {VEHICLE_STATUS_LABELS[status] || t('vehicles.unknownStatus')}
          </span>
        );
      }
    }
  ];

  const actions = [
    {
      icon: <Eye className="h-4 w-4" />,
      label: t('vehicles.actions.view'),
      onClick: (row) => navigate(`/admin/veiculos/${row._id}`),
      className: 'text-gray-600 hover:text-primary'
    },
    {
      icon: <Edit className="h-4 w-4" />,
      label: t('vehicles.actions.edit'),
      onClick: (row) => navigate(`/admin/veiculos/${row._id}/edit`),
      className: 'text-gray-600 hover:text-primary'
    },
    {
      icon: <Trash2 className="h-4 w-4" />,
      label: t('vehicles.actions.delete'),
      onClick: handleDeleteClick,
      className: 'text-red-600 hover:text-red-700'
    }
  ];

  // Função removida (handleImport) que não estava sendo utilizada

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('vehicles.title')}
        description={t('vehicles.description')}
      >
        <div className="flex flex-wrap gap-2">
          <Button
            variant="default"
            onClick={() => navigate('/admin/veiculos/create')}
            className="flex items-center gap-1"
          >
            <Plus className="h-4 w-4" />
            {t('vehicles.newVehicle')}
          </Button>
          
          <Button
            variant="outline"
            onClick={() => setImportModalOpen(true)}
            className="flex items-center gap-1"
          >
            <Upload className="h-4 w-4" />
            {t('vehicles.importSpreadsheet')}
          </Button>
        </div>
      </PageHeader>

      <div className="space-y-4">
        {/* Filtros de busca */}
        <div className="flex items-center gap-4 flex-wrap">
          {/* Busca por placa */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('vehicles.searchPlaceholder')}
                value={placaFilter}
                onChange={(e) => setPlacaFilter(e.target.value)}
                className="pl-9 w-48"
              />
            </div>
          </div>

          {/* Filtro por status */}
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder={t('vehicles.filterByStatus')} />
              </SelectTrigger>
              <SelectContent>
                {VEHICLE_STATUS_OPTIONS.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Botão limpar filtros */}
          {(placaFilter || statusFilter) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPlacaFilter('');
                setStatusFilter('');
              }}
              className="text-muted-foreground hover:text-foreground"
            >
              {t('vehicles.clearFilters')}
            </Button>
          )}
        </div>

        <ShadcnDataTable
          columns={columns}
          data={filteredData}
          loading={loading}
          actions={actions}
          onRowClick={(row) => navigate(`/admin/veiculos/${row._id}`)}
        />

        {/* Paginação */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {t('vehicles.pagination.showing', {
              start: pagination.totalItems === 0 ? 0 : (pagination.currentPage - 1) * pagination.itemsPerPage + 1,
              end: Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems),
              total: pagination.totalItems
            })}
          </div>
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={(page) => {
              setCurrentPage(page);
              if (placaFilter || statusFilter) {
                searchWithFilters(page);
              }
            }}
          />
        </div>
      </div>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('vehicles.confirmDelete.title')}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              {t('vehicles.confirmDelete.messagePrefix')} <strong>{vehicleToDelete?.placa}</strong>?
            </p>
            {vehicleToDelete?.infoTecnica?.marca && vehicleToDelete?.infoTecnica?.modelo && (
              <p className="text-sm text-muted-foreground mt-1">
                {vehicleToDelete.infoTecnica.marca} {vehicleToDelete.infoTecnica.modelo}
              </p>
            )}
            <p className="text-sm text-muted-foreground mt-2 font-medium text-red-600">
              {t('vehicles.confirmDelete.warning')}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleDeleteCancel} disabled={deleting}>
              {t('vehicles.confirmDelete.cancel')}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteConfirm} 
              disabled={deleting}
            >
              {deleting ? t('vehicles.confirmDelete.deleting') : t('vehicles.confirmDelete.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de importação de planilha */}
      <Dialog open={importModalOpen} onOpenChange={setImportModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <ImportExcelModal 
            onClose={() => setImportModalOpen(false)}
            onSuccess={(result) => {
              loadVehicles();
              
              if (result.success) {
                toast({
                  variant: "success",
                  title: t('vehicles.import.completed'),
                  description: result.hasErrors 
                    ? t('vehicles.import.completedWithWarnings', { count: result.count })
                    : t('vehicles.import.completedSuccess', { count: result.count })
                });
              } else {
                toast({
                  variant: "destructive",
                  title: t('vehicles.import.error'),
                  description: t('vehicles.import.errorDescription')
                });
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}